<template>
    <div class="similar-products-slider">
        <div class="flex flex-row justify-between items-center mb-16">
            <h3 class="base_bold">{{ $t('product_similar') }}</h3>
            <div class="flex flex-row justify-end gap-4">
                <div class="similar-products-button-prev text-blue cursor-pointer">
                    <Icon type="TailLeft" viewBox="0 0 30 21" width="25" height="26" fill="none"
                          class="stroke-current"/>
                </div>
                <div class="similar-products-button-next text-blue cursor-pointer">
                    <Icon type="TailRight" viewBox="0 0 30 21" width="25" height="26" fill="none"
                          class="stroke-current"/>
                </div>
            </div>
        </div>

        <div class="swiper-wrapper" style="height: max-content">
            <div
                class="swiper-slide"
                v-for="product_group in product_groups_filtered"
                :key="product_group.id"
                @click="onSlideClick(product_group)"
            >
                <ProductGroupSingle
                    :data="product_group"
                    class="bg-white h-auto"
                    :backgroundColor="category.background_color"
                    :backgroundGradient="category.background_gradient"
                />
            </div>
        </div>

        <div class="swiper-scrollbar"></div>
    </div>
</template>

<script>
import Swiper, {Navigation} from 'swiper';

Swiper.use([Navigation]);

import _ from 'lodash';
import {mapGetters} from 'vuex';

export default {
    name: 'SimilarProducts',

    props: ['category', 'except'],

    data: () => ({
        loading: true,
        _swiper: null
    }),

    computed: {
        // Product groups by category
        ...mapGetters('product_groups', {
            getByProductCategoryId: 'getByProductCategoryId'
        }),
        // Global loading flag
        ...mapGetters('settings', {
            isAllLoaded: 'isAllLoaded'
        }),
        // Product group detail store (used to get variants)
        ...mapGetters('product_group', {
            getProduct: 'getProduct',
            getProductAttributesByType: 'getProductAttributesByType',
            getProductGroup: 'get'            // Full group by ID
        }),

        product_groups_filtered() {
            const product_groups = this.getByProductCategoryId(this.category.id)?.data?.data;
            if (!product_groups) return [];

            if (this.except) {
                return product_groups.filter((pg) => pg.id !== this.except);
            }
            return product_groups;
        }
    },

    watch: {
        isAllLoaded() {
            this.loading = false;
            this.$nextTick(() => this.init());
        },
        product_groups_filtered() {
            this.$nextTick(() => this.init());
        }
    },

    mounted() {
        this.$nextTick(() => this.init());
    },

    methods: {
        // Initialize swiper (destroy old instance if already running)
        init() {
            if (this._swiper) {
                this._swiper.destroy(true, true);
                this._swiper = null;
            }
            this._swiper = new Swiper('.similar-products-slider', {
                slidesPerView: 1,
                spaceBetween: 10,
                navigation: {
                    nextEl: '.similar-products-button-next',
                    prevEl: '.similar-products-button-prev'
                },
                breakpoints: {
                    480: {slidesPerView: 1},
                    640: {slidesPerView: 1},
                    1024: {slidesPerView: 2},
                    1200: {slidesPerView: 2}
                }
            });
        },

        // Handle click on a similar product card
        async onSlideClick(pg) {
            try {
                // Load full product group with variants into store
                await this.ensurePgLoaded(pg.id);

                // Select default variant (shortest period like in product.vue)
                const variants = this.getProduct?.productVariants?.data || [];
                const variant = this.pickDefaultVariant(variants);

                // NACH dem Laden: die volle Gruppe aus dem Store nehmen
                const fullGroup = this.getProductGroup || pg;

                // Push GA4 view_item (mit item_list_name im Item)
                this.pushViewItemFromSimilar(fullGroup, variant, 'Product Detail — Similar Products');

                // Emit to parent (optional, e.g. for navigation)
                this.$emit('product-click', pg, variant);
            } catch (e) {
                // Fallback if something goes wrong
                const fullGroup = this.getProductGroup || pg;
                this.pushViewItemFromSimilar(fullGroup, null, 'Product Detail — Similar Products');
                this.$emit('product-click', pg, null);
            }
        },

        async ensurePgLoaded(pgId) {
            await this.$store.dispatch('product_group/show', pgId);
        },

        pickDefaultVariant(variants) {
            if (!Array.isArray(variants) || !variants.length) return null;

            // Sort by period runtime_in_days ascending
            const withPeriod = variants.map((v) => {
                const p = this.getAttributeByType(v, 'period');
                return {v, days: p?.meta?.runtime_in_days ?? Number.MAX_SAFE_INTEGER};
            });
            return _.sortBy(withPeriod, (x) => x.days).map((x) => x.v)[0] || null;
        },

        getAttributeByType(productVariant, type) {
            return productVariant?.productAttributeValues?.data?.find((pav) => pav?.meta?.type === type);
        },

        pushViewItemFromSimilar(productGroup, productVariant, listName = 'Product Detail — Similar Products') {
            // baue Item inkl. item_list_name
            const item = this.buildGa4Item(productGroup, productVariant, 0);
            if (!item) return;

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ecommerce: null});
            window.dataLayer.push({
                event: 'view_item',
                ecommerce: {
                    // GA4 ignoriert item_list_name bei view_item in Standardreports,
                    // aber es steht im Data Layer und kann via Custom Dimension genutzt werden.
                    item_list_name: listName,
                    currency: item.currency,
                    value: item.price,
                    items: [item]
                }
            });
        },

        // ===== GA4 Helpers (copied from product.vue, extended with item_list_name) =====
        buildGa4Item(productGroup, productVariant, index = 0) {
            if (!productGroup || !productVariant) return null;

            const item_id = `${productVariant.id}`;

            const item_name =
                (this.$translate && this.$translate(productGroup?.name)) ||
                productGroup?.name?.de ||
                productGroup?.name ||
                'Product';

            let item_category =
                (this.$translate && this.$translate(productGroup?.productCategories?.data?.[0]?.name)) ||
                productGroup?.productCategories?.data?.[0]?.name?.de ||
                productGroup?.productCategories?.data?.[0]?.name;
            // 2) Fallback: nimm die Komponent-Prop `category`
            if (!item_category && this.category) {
                item_category = (this.$translate && this.$translate(this.category?.name)) ||
                    this.category?.name?.de ||
                    this.category?.name;
            }
            // 3) Letzter Fallback
            if (!item_category) item_category = 'Category';

            const periodAttr = this.getAttributeByType(productVariant, 'period');
            const item_variant =
                (this.$translate && this.$translate(periodAttr?.title)) ||
                periodAttr?.title?.de ||
                periodAttr?.title ||
                'Variant';

            const base = Number(productVariant?.base_price_gross ?? 0);
            const discountPercent = Number(productVariant?.discount_percent ?? 0);
            const price = Number((base - base * discountPercent).toFixed(2));

            const currency = (this.$getCurrencyCode && this.$getCurrencyCode()) || 'EUR';

            return {
                item_id,
                item_name,
                item_category,
                item_variant,
                price,
                currency,
                quantity: 1,
                index
            };
        },


    }
};
</script>
